import { ChangeDetectionStrategy, Component, inject, signal, DestroyRef, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { AccessibilityService } from '../../../shared/services/accessibility.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, of } from 'rxjs';
import { containsDangerousPatterns } from '@shared/utils/html-security.utils';

@Component({
  selector: 'app-accessibility-editor',
  templateUrl: './accessibility-editor.component.html',
  styleUrls: ['./accessibility-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AccessibilityEditorComponent {
  private readonly accessibilityService = inject(AccessibilityService);
  private readonly destroyRef = inject(DestroyRef);

  // Signals for reactive state management
  accessibilityContent = signal('');
  originalAccessibilityContent = signal('');
  isAccessibilityEnabled = signal(true);
  isLoading = signal(false);
  isSaving = signal(false);
  isUpdatingStatus = signal(false);
  saveMessage = signal('');
  securityWarning = signal('');
  statusMessage = signal('');

  constructor() {
    this.loadAccessibilityContent();
  }

  loadAccessibilityContent(): void {
    this.isLoading.set(true);

    // Use admin API to get content even when accessibility is disabled
    this.accessibilityService.getAccessibilityContentForAdmin().pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(error => {
        console.error('Error loading accessibility content:', error);
        this.saveMessage.set('Error loading accessibility content. Please try again.');
        return of({ content: '<h2>Accessibility Information</h2>\n<p>Please enter your accessibility information content here...</p>', isAccessibilityEnabled: true });
      }),
      finalize(() => this.isLoading.set(false))
    ).subscribe(response => {
      const content = response.content || '';
      this.accessibilityContent.set(content);
      this.originalAccessibilityContent.set(content);
      this.isAccessibilityEnabled.set(response.isAccessibilityEnabled ?? true);
    });
  }

  onAccessibilityContentChange(): void {
    this.saveMessage.set('');
    this.securityWarning.set('');
  }

  saveAccessibilityContent(): void {
    if (this.isSaving()) return;

    this.isSaving.set(true);
    this.saveMessage.set('');

    const originalContent = this.accessibilityContent();
    
    // Validate content is not empty
    if (!this.isValidAccessibilityContent(originalContent)) {
      this.saveMessage.set('Accessibility content cannot be empty. Please enter some content.');
      this.isSaving.set(false);
      return;
    }

    // Check for dangerous content patterns
    if (containsDangerousPatterns(originalContent)) {
      this.showSecurityWarning();
    }

    // Send original content to backend - backend HtmlContentSanitizer handles security
    this.accessibilityService.saveAccessibilityContent(originalContent).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(error => {
        console.error('Error saving accessibility content:', error);
        this.saveMessage.set('Error saving accessibility content. Please try again.');
        return of(null);
      }),
      finalize(() => this.isSaving.set(false))
    ).subscribe(response => {
      if (response) {
        this.saveMessage.set('Accessibility content saved successfully!');

        // Reload content from backend to display the sanitized version
        this.loadAccessibilityContent();

        // Clear success message after 3 seconds
        setTimeout(() => this.saveMessage.set(''), 3000);
      }
    });
  }

  toggleAccessibilityStatus(): void {
    if (this.isUpdatingStatus()) return;

    const newStatus = !this.isAccessibilityEnabled();
    this.isUpdatingStatus.set(true);
    this.statusMessage.set('');

    this.accessibilityService.toggleAccessibilityStatus(newStatus).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(error => {
        console.error('Error updating accessibility status:', error);
        this.statusMessage.set('Error updating accessibility status. Please try again.');
        return of(null);
      }),
      finalize(() => this.isUpdatingStatus.set(false))
    ).subscribe(response => {
      if (response) {
        this.isAccessibilityEnabled.set(newStatus);
        this.statusMessage.set(`Accessibility ${newStatus ? 'enabled' : 'disabled'} successfully!`);

        // Clear status message after 3 seconds
        setTimeout(() => this.statusMessage.set(''), 3000);
      }
    });
  }

  hasUnsavedChanges(): boolean {
    return this.accessibilityContent() !== this.originalAccessibilityContent();
  }

  resetChanges(): void {
    this.accessibilityContent.set(this.originalAccessibilityContent());
    this.saveMessage.set('');
    this.securityWarning.set('');
  }

  discardChanges(): void {
    this.resetChanges();
  }

  private showSecurityWarning(): void {
    this.securityWarning.set('Potentially harmful content will be sanitized by the server before saving.');

    // Clear warning after 5 seconds
    setTimeout(() => this.securityWarning.set(''), 5000);
  }

  private isValidAccessibilityContent(content: string): boolean {
    return !(!content || content.trim() === '');
  }

  // Helper methods for template
  getStatusText(): string {
    return this.isAccessibilityEnabled() ? 'Enabled' : 'Disabled';
  }

  getStatusClass(): string {
    return this.isAccessibilityEnabled() ? 'enabled' : 'disabled';
  }

  getWarningMessage(): string {
    return this.isAccessibilityEnabled()
      ? ''
      : 'Accessibility information is currently disabled. Users will not see accessibility information when accessing the portal.';
  }
}
